import {PermissionRepository} from './PermissionRepository';
import {UserAPIDataSource} from '../api/UserAPIDataSource';
import {
  Permission,
  CreatePermissionRequest,
  UpdatePermissionRequest,
  PermissionFilters,
  AssignUserPermissionRequest,
  DenyUserPermissionRequest,
  UserEffectivePermissions
} from '../models/permission.model';
import {AppError} from '../../../Data/config/AppError';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {mapPermissionsListResponseToModel, mapPermissionDTOToModel, mapUserEffectivePermissionsDTOToModel} from '../mappers/permission.mapper';

export class PermissionRepositoryImpl implements PermissionRepository {
  private readonly userDataSource: UserAPIDataSource;

  constructor () {
    this.userDataSource = new UserAPIDataSource();
  }

  async createPermission (permissionData: CreatePermissionRequest): Promise<Permission> {
    try {
      const response = await this.userDataSource.createPermission(permissionData);
      return mapPermissionDTOToModel(response);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async getPermissions (filters?: PermissionFilters): Promise<{ permissions: Permission[]; pagination: any }> {
    try {
      const defaultFilters = {
        page: 1,
        limit: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
        ...filters
      };

      const response = await this.userDataSource.getPermissions(defaultFilters);
      return mapPermissionsListResponseToModel(response);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async getAllPermissions (): Promise<{ permissions: Permission[]; pagination: any }> {
    try {
      // Fetch with a large limit to get all permissions
      const filters = {
        page: 1,
        limit: 1000 // Large limit to get all permissions
      };

      const response = await this.userDataSource.getPermissions(filters);
      const result = mapPermissionsListResponseToModel(response);

      return result;
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async getPermissionById (id: number): Promise<Permission> {
    try {
      const response = await this.userDataSource.getPermissionById(id);
      return mapPermissionDTOToModel(response);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async updatePermission (id: number, permissionData: UpdatePermissionRequest): Promise<Permission> {
    try {
      const response = await this.userDataSource.updatePermission(id, permissionData);
      return mapPermissionDTOToModel(response);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async deletePermission (id: number): Promise<void> {
    try {
      await this.userDataSource.deletePermission(id);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async assignUserPermission (data: AssignUserPermissionRequest): Promise<void> {
    try {
      await this.userDataSource.assignUserPermission(data);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async denyUserPermission (data: DenyUserPermissionRequest): Promise<void> {
    try {
      await this.userDataSource.denyUserPermission(data);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async getUserPermissions (userId: number): Promise<Permission[]> {
    try {
      const response = await this.userDataSource.getUserPermissions(userId);
      return response.map(mapPermissionDTOToModel);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async getUserEffectivePermissions (userId: number): Promise<UserEffectivePermissions> {
    try {
      const response = await this.userDataSource.getUserEffectivePermissions(userId);
      return mapUserEffectivePermissionsDTOToModel(response);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async removeUserPermission (userId: number, permissionId: number): Promise<void> {
    try {
      await this.userDataSource.removeUserPermission(userId, permissionId);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async getPermissionStatistics (): Promise<{
    total: number;
    byResource: Array<{ resource: string; count: number }>;
    byAction: Array<{ action: string; count: number }>;
  }> {
    try {
      const response = await this.userDataSource.getPermissionStatistics();
      return response;
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }
}
